import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import { showToast } from '@/hooks/useToast';

interface JourneyListenedState {
  listenedSongIds: number[];
  loading: boolean;
  setLoading: (loading: boolean) => void;
  setListenedSongIds: (ids: number[]) => void;
  fetchListenedSongs: (userId: string) => Promise<void>;
  markAsListened: (userId: string, songId: number, isPremiumSong: boolean, userType: string) => Promise<void>;
  unmarkAsListened: (userId: string, songId: number) => Promise<void>;
  clearState: () => void;
  progress: {
    total: number;
    listened: number;
    percentage: number;
  };
}

const TOTAL_SONGS = 198;

const calculateProgress = (listenedSongIds: number[]) => ({
  total: TOTAL_SONGS,
  listened: listenedSongIds.length,
  percentage: Math.round((listenedSongIds.length / TOTAL_SONGS) * 100)
});

const initialState = {
  listenedSongIds: [],
  loading: false,
  progress: calculateProgress([])
};

export const useJourneyListenedStore = create<JourneyListenedState>((set, get) => ({
  ...initialState,
  setLoading: (loading) => set({ loading }),
  setListenedSongIds: (ids) => set({ 
    listenedSongIds: ids,
    progress: calculateProgress(ids)
  }),

  clearState: () => set(initialState),

  fetchListenedSongs: async (userId) => {
    try {
      if (!userId) {
        set({ 
          listenedSongIds: [], 
          loading: false,
          progress: calculateProgress([])
        });
        return;
      }

      const { data, error } = await supabase
        .from('journey_listened_songs')
        .select('song_id')
        .eq('user_id', userId);

      if (error) throw error;

      const ids = data?.map(item => item.song_id) || [];
      set({ 
        listenedSongIds: ids,
        progress: calculateProgress(ids)
      });
    } catch (error) {
      console.error('Erro ao buscar músicas ouvidas:', error);
      showToast('Erro ao carregar músicas ouvidas', 'error');
    } finally {
      set({ loading: false });
    }
  },

  markAsListened: async (userId, songId, isPremiumSong, userType) => {
    try {
      if (!userId) return;

      // Verifica se o usuário pode marcar a música como ouvida
      if (isPremiumSong && userType !== 'premium') {
        showToast('Apenas usuários premium podem marcar músicas premium como ouvidas', 'error');
        return;
      }

      const { error } = await supabase
        .from('journey_listened_songs')
        .insert([{ user_id: userId, song_id: songId }]);

      if (error) throw error;

      const currentIds = get().listenedSongIds;
      const newIds = [...currentIds, songId];
      set({ 
        listenedSongIds: newIds,
        progress: calculateProgress(newIds)
      });
      showToast('Música marcada como ouvida! 🎵', 'success');
    } catch (error) {
      console.error('Erro ao marcar música como ouvida:', error);
      showToast('Erro ao marcar música como ouvida', 'error');
    }
  },

  unmarkAsListened: async (userId, songId) => {
    try {
      if (!userId) return;

      const { error } = await supabase
        .from('journey_listened_songs')
        .delete()
        .eq('user_id', userId)
        .eq('song_id', songId);

      if (error) throw error;

      const currentIds = get().listenedSongIds;
      const newIds = currentIds.filter(id => id !== songId);
      set({ 
        listenedSongIds: newIds,
        progress: calculateProgress(newIds)
      });
      showToast('Música desmarcada como ouvida', 'info');
    } catch (error) {
      console.error('Erro ao desmarcar música:', error);
      showToast('Erro ao desmarcar música', 'error');
    }
  }
})); 